import { useDropdown } from "../../DropDown/hooks/useDropdown";
import { useEffect, useMemo, useRef, useState } from "react";
import { Event } from "@/types/Event";
import { useLocation, useNavigate as useRouter } from "react-router-dom";
import {
  createPinnedEvent,
  deletePinnedEvent,
} from "@/lib/api/clients/PinnedEventClient";
import { getEvents } from "@/lib/api/handlers/EventHandling";
import { lastPressWasTouch } from "@/lib/utils/pointer";
import { getEventSwitchHref } from "./getEventSwitchHref";

// Define the event item structure with eventId
type EventItem = {
  eventId: string;
  name: string;
  pinned: boolean;
  // Add a category to track the source of the event
  category: "pinned" | "recent" | "other";
};

export const useEventSwitcher = ({
  eventId,
  initialItem,
  eventsProp,
}: {
  eventId?: string;
  initialItem: string;
  eventsProp: {
    validEvents: Event[];
    validPinnedEvents: Event[];
    validRecentEvents: Event[];
  };
}) => {
  const router = useRouter();
  const pathname = useLocation().pathname;
  const inputRef = useRef<HTMLInputElement>(null);
  // Memoised — a ref would freeze at first mount and miss pin/unpin/refetch.
  const totalQuantityEvents = useMemo(
    () =>
      (eventsProp?.validEvents?.length || 0) +
      (eventsProp?.validPinnedEvents?.length || 0) +
      (eventsProp?.validRecentEvents?.length || 0),
    [eventsProp],
  );
  const [search, setSearch] = useState("");
  const [loadingNavigation, setLoadingNavigation] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Single ref for all events
  const allEventsRef = useRef<EventItem[]>([]);

  // Track searches that returned no results
  const noResultsSearchesRef = useRef<Set<string>>(new Set());

  // Active items to display based on search and sorting
  const [activeItems, setActiveItems] = useState<EventItem[]>([]);

  // Helper function to sort events within their category
  const sortEventsByName = (events: EventItem[]) => {
    if (!events || events.length === 0) return [];
    return [...events].sort((a, b) => a.name.localeCompare(b.name));
  };

  // Helper to group and order events by category
  const getOrderedEventsByCategory = (events: EventItem[]) => {
    if (!events || events.length === 0) return [];

    // Separate events by category
    const pinnedEvents = events.filter((item) => item.pinned);
    const recentEvents = events.filter(
      (item) => !item.pinned && item.category === "recent",
    );
    const otherEvents = events.filter(
      (item) => !item.pinned && item.category === "other",
    );

    // Sort each category alphabetically
    const sortedPinned = sortEventsByName(pinnedEvents);
    const sortedOther = sortEventsByName(otherEvents);

    // Return combined array in the correct order
    return [...sortedPinned, ...recentEvents, ...sortedOther];
  };

  // Initialize events
  useEffect(() => {
    function handleEvents() {
      // Process pinned events - handle empty/undefined arrays
      const pinnedEvents = (eventsProp?.validPinnedEvents || [])
        .filter((event: Event) => event?.eventId && event?.name)
        .map((event: Event) => ({
          eventId: event.eventId as string,
          name: event.name as string,
          pinned: true,
          category: "pinned" as const,
        }));

      // Process recent events - handle empty/undefined arrays
      const recentEvents = (eventsProp?.validRecentEvents || [])
        .filter((event: Event) => event?.eventId && event?.name)
        .map((event: Event) => ({
          eventId: event.eventId as string,
          name: event.name as string,
          pinned: false,
          category: "recent" as const,
        }));

      // Process other events - handle empty/undefined arrays
      const otherEvents = (eventsProp?.validEvents || [])
        .filter((event: Event) => event?.eventId && event?.name)
        .map((event: Event) => ({
          eventId: event.eventId as string,
          name: event.name as string,
          pinned: false,
          category: "other" as const,
        }));

      // Sort each category alphabetically
      const sortedPinnedEvents = sortEventsByName(pinnedEvents);
      const sortedOtherEvents = sortEventsByName(otherEvents);

      // Combine all events - this will handle empty arrays gracefully
      let allEvents = [
        ...sortedPinnedEvents,
        ...recentEvents,
        ...sortedOtherEvents,
      ];

      // Filter out current event if needed
      if (eventId) {
        allEvents = allEvents.filter((item) => item.eventId !== eventId);
      }

      // Store in ref
      allEventsRef.current = allEvents;

      // Set active items
      setActiveItems(allEvents);
    }

    handleEvents();
  }, [eventsProp, eventId]); // Added dependencies

  // SAppBar is not keyed by eventId, so on react-query cache hits this hook
  // does not unmount — reset transient state ourselves on event switch.
  useEffect(() => {
    setSearch("");
    noResultsSearchesRef.current = new Set();
  }, [eventId]);

  // isCancelled lets the caller abort us across event/search changes —
  // getEvents itself does not accept an AbortSignal.
  const searchEventsFromDb = async (
    searchTerm: string,
    isCancelled: () => boolean,
  ) => {
    if (searchTerm?.length < 3) return; // API requires at least 3 characters

    // Check if this search is a refinement of a previous search that returned no results
    if (searchTerm?.length >= 3) {
      // Check if any previous no-results search is a substring of the current search
      for (const prevSearch of noResultsSearchesRef.current) {
        // If current search contains a previous failed search, it will also return no results
        if (searchTerm.includes(prevSearch)) return;
      }
    }

    try {
      if (isCancelled()) return;
      setIsSearching(true);
      const results = await getEvents(
        "eventId,name",
        searchTerm,
        0,
        20,
        [],
        "name",
        "asc",
      );

      if (isCancelled()) return;

      // If the search returned no results, add it to our set
      if (!results || !Array.isArray(results) || results?.length === 0) {
        if (searchTerm?.length >= 3) {
          noResultsSearchesRef.current.add(searchTerm);
        }
        return;
      }

      // Process results
      if (results && Array.isArray(results)) {
        // Convert API results to EventItem format
        const searchResults = results
          .filter((event) => event?.eventId && event?.name)
          .map((event) => {
            // Check if this event already exists in our ref
            const existingEvent = allEventsRef.current.find(
              (item) => item.eventId === event.eventId,
            );

            if (existingEvent) {
              // Use existing event's pinned status and category
              return existingEvent;
            } else {
              // Create new event item with "other" category and not pinned
              return {
                eventId: event.eventId as string,
                name: event.name as string,
                pinned: false,
                category: "other" as const,
              };
            }
          });

        // Filter out the current event if needed
        const filteredResults = eventId
          ? searchResults.filter((item) => item.eventId !== eventId)
          : searchResults;

        // Add any new events to allEventsRef that aren't already there
        filteredResults.forEach((result) => {
          const exists = allEventsRef.current.some(
            (item) => item.eventId === result.eventId,
          );

          if (!exists) {
            allEventsRef.current.push(result);
          }
        });

        if (isCancelled()) return;
        // Update active items with search results
        setActiveItems(getOrderedEventsByCategory(filteredResults));
      }
    } catch {
      return [];
    } finally {
      if (!isCancelled()) setIsSearching(false);
    }
  };

  // Handle search functionality
  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    const filterEvents = () => {
      if (cancelled) return;
      if (!search.trim()) {
        // If search is empty, show all events in category order
        setActiveItems(getOrderedEventsByCategory(allEventsRef.current));
        return;
      }

      const searchLower = search.toLowerCase().trim();

      // Local filtering first
      const filteredEvents = allEventsRef.current.filter((item) =>
        item.name.toLowerCase().includes(searchLower),
      );

      // Set active items with local results first
      setActiveItems(getOrderedEventsByCategory(filteredEvents));

      // If we have more than 20 events, perform API search
      if (allEventsRef.current?.length >= 20 && search?.length >= 3) {
        searchEventsFromDb(search, isCancelled);
      }
    };

    // Debounce search to avoid too many API calls
    const debounceTimer = setTimeout(filterEvents, 300);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
    };
  }, [search, eventId]);

  // Clear the covering spinner once the route has changed. Without this,
  // when SAppBar's initialiseUser query is cache-hit (within staleTime),
  // EventSwitcher never unmounts and loadingNavigation would stay true forever.
  useEffect(() => {
    setLoadingNavigation(false);
  }, [pathname]);

  const { isDropdownOpen, selectedItem, setIsDropdownOpen, handleSelect } =
    useDropdown({
      initialItem,
      onlyAction: true,
      allItems: activeItems.map((item) => item.name),
      onDropdownAction: (item) => {
        if (item) {
          setLoadingNavigation(true);
          router(getEventSwitchHref(item, pathname));
        }
      },
    });

  // Apply category sorting and reorganization only when dropdown opens
  useEffect(() => {
    function reorganizeEvents() {
      // Update categories based on pin status
      allEventsRef.current = allEventsRef.current.map((item) => {
        // If item is pinned, ensure it has the pinned category
        if (item.pinned && item.category !== "pinned") {
          return { ...item, category: "pinned" };
        }

        // If item was unpinned from the pinned category, place it in its original category
        // (We're not changing categories for already unpinned items)
        if (!item.pinned && item.category === "pinned") {
          // Here we could determine the original category if we tracked it
          // For now, default to "other" for previously pinned items
          return { ...item, category: "other" };
        }

        return item;
      });

      // Update active items based on current search
      const searchLower = search.toLowerCase().trim();
      if (searchLower) {
        const filteredEvents = allEventsRef.current.filter((item) =>
          item.name.toLowerCase().includes(searchLower),
        );
        setActiveItems(getOrderedEventsByCategory(filteredEvents));
      } else {
        setActiveItems(getOrderedEventsByCategory(allEventsRef.current));
      }
    }

    if (isDropdownOpen) {
      // Focus the input when dropdown opens — but not when THIS interaction
      // was a touch (per-event check, so hybrids count too): tap-autofocus
      // pops the keyboard over the list, and (pre-CSS-guard) the focused
      // 12px input triggered iOS Safari's page zoom.
      if (inputRef.current && !lastPressWasTouch()) inputRef.current.focus();

      // Reorganize events when dropdown opens
      if (allEventsRef.current?.length > 0) {
        reorganizeEvents();
      }
    }
  }, [isDropdownOpen, search]);

  function togglePin(eventId: string) {
    // Find the event in all events
    const eventIndex = allEventsRef.current.findIndex(
      (item) => item.eventId === eventId,
    );

    if (eventIndex === -1) return;

    // Get current pinned state and toggle it
    const event = allEventsRef.current[eventIndex];
    const isPinned = !event.pinned;

    // Check pinned count limit
    if (isPinned) {
      const pinnedCount =
        allEventsRef.current.filter((item) => item.pinned)?.length || 0; // Added fallback for empty array
      if (pinnedCount >= 7) {
        console.warn("Maximum of 7 pinned events reached.");
        return;
      }
    }

    // Only update pin state in the item (no category change or reordering yet)
    allEventsRef.current = allEventsRef.current.map((item) =>
      item.eventId === eventId ? { ...item, pinned: isPinned } : item,
    );

    // Find and update the item in activeItems without reordering
    setActiveItems((prevItems) =>
      prevItems.map((item) =>
        item.eventId === eventId ? { ...item, pinned: isPinned } : item,
      ),
    );

    // Perform API call
    if (isPinned) {
      createPinnedEvent(eventId).catch((error) =>
        console.error("Failed to pin event:", error),
      );
    } else {
      deletePinnedEvent(eventId).catch((error) =>
        console.error("Failed to unpin event:", error),
      );
    }
  }

  return {
    setIsDropdownOpen,
    selectedItem,
    isDropdownOpen,
    sortedItems: activeItems, // Keep interface consistent
    handleSelect,
    togglePin,
    loadingNavigation,
    inputRef,
    search,
    setSearch,
    isSearching,
    totalQuantityEvents,
    getHrefForEvent: (targetEventId: string) =>
      getEventSwitchHref(targetEventId, pathname),
  };
};
